import React, { Component } from "react";
import PropTypes from "prop-types";

import moment from "moment";

import {
  TINCAN_USERNAME,
  TINCAN_PASSKEY,
  TINCAN_ENDPOINT,
  TINCAN_VALID_PROJECTS,
  TINCAN_VALID_TOPICS,
  TINCAN_VALID_CHALLENGES,
  PLAYER_URL
} from "config";
import localize from "lang/localize";
import deviceServices from "services/deviceServices";

import TinCan from "tincanjs";

const propTypes = {
  id: PropTypes.string,
  user: PropTypes.object,
  contentId: PropTypes.number,
  contentTitle: PropTypes.string,
  project: PropTypes.object,
  topic: PropTypes.object
};

// Methods copied from https://stackoverflow.com/a/54921282
class YoutubePlayer extends Component {
  lastPlayerState = null;
  lastPlayerTime = null;
  lrs = null;
  youtubeUrlPrepended = "http://www.youtube.com/watch?v=";

  componentDidMount = () => {
    try {
      this.lrs = new TinCan.LRS({
        endpoint: TINCAN_ENDPOINT,
        username: TINCAN_USERNAME,
        password: TINCAN_PASSKEY,
        allowFail: false
      });
    } catch (ex) {
      // Error handling
      this.lrs = null;
    }

    // On mount, check to see if the API script is already loaded
    if (!window.YT) {
      // If not, load the script asynchronously
      const tag = document.createElement("script");
      tag.src = "https://www.youtube.com/iframe_api";

      // onYouTubeIframeAPIReady will load the video after the script is loaded
      window.onYouTubeIframeAPIReady = this.loadVideo;

      const firstScriptTag = document.getElementsByTagName("script")[0];
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
    } else {
      // If script is already there, load the video directly
      this.loadVideo();
    }
  };

  loadVideo = () => {
    const { id } = this.props;

    // the Player object is created uniquely based on the id in props
    this.player = new window.YT.Player(`youtube-player-${id}`, {
      videoId: id,
      events: {
        onReady: this.onPlayerReady,
        onStateChange: this.onPlayerStateChange
      }
    });
  };

  /* Does nothing right now */
  onPlayerReady = event => {};

  onPlayerStateChange = event => {
    /* Initialize formatted time variables */
    let lastPlayerTimeInt = parseInt(this.lastPlayerTime, 10);
    let lastPlayerTimeFormatted = moment("2000-01-01 00:00:00")
      .add(moment.duration(lastPlayerTimeInt, "seconds"))
      .format("HH:mm:ss");
    let currentTimeInt = parseInt(this.player.getCurrentTime(), 10);
    let currentTimeFormatted = moment("2000-01-01 00:00:00")
      .add(moment.duration(currentTimeInt, "seconds"))
      .format("HH:mm:ss");

    /* Initialize other Youtube player data */
    let videoTitle = event.target.getVideoData().title;
    let videoId = event.target.getVideoData().video_id;

    /* Initialize boolean variable for Projects tracked */
    const IS_TRACKED_PROJECT =
      (typeof TINCAN_VALID_PROJECTS === "boolean" &&
        TINCAN_VALID_PROJECTS === true) ||
      (Array.isArray(TINCAN_VALID_PROJECTS) &&
        this.props.project &&
        TINCAN_VALID_PROJECTS.indexOf(this.props.project.id) !== -1);
    const IS_TRACKED_TOPIC =
      (typeof TINCAN_VALID_TOPICS === "boolean" &&
        TINCAN_VALID_TOPICS === true) ||
      (Array.isArray(TINCAN_VALID_TOPICS) &&
        this.props.topic &&
        TINCAN_VALID_TOPICS.indexOf(this.props.topic.id) !== -1);
    const IS_TRACKED_CHALLENGE =
      (typeof TINCAN_VALID_CHALLENGES === "boolean" &&
        TINCAN_VALID_CHALLENGES === true) ||
      (Array.isArray(TINCAN_VALID_CHALLENGES) &&
        this.props.contentId &&
        TINCAN_VALID_CHALLENGES.indexOf(this.props.contentId) !== -1);

    /* Logged-in player exists, and LRS exists */
    /* localize will be used in "en", instead of the usual this.props.language */
    if (
      this.props.user &&
      this.props.user.id &&
      this.lrs &&
      (IS_TRACKED_PROJECT || IS_TRACKED_TOPIC || IS_TRACKED_CHALLENGE)
    ) {
      switch (event.data) {
        case window.YT.PlayerState.PLAYING:
          if (this.lastPlayerState === window.YT.PlayerState.PAUSED) {
            this.videoResumed(videoTitle, videoId);
          } else {
            this.videoStarted(videoTitle, videoId);
          }
          break;
        case window.YT.PlayerState.PAUSED:
          if (this.lastPlayerState === window.YT.PlayerState.PLAYING) {
            this.videoPausedAfterPlaying(
              videoTitle,
              videoId,
              lastPlayerTimeFormatted,
              currentTimeFormatted
            );
          } else if (this.lastPlayerState === window.YT.PlayerState.PAUSED) {
            this.videoSkipped(
              videoTitle,
              videoId,
              lastPlayerTimeFormatted,
              currentTimeFormatted
            );
          }
          break;
        case window.YT.PlayerState.ENDED:
          this.videoTerminated(videoTitle, videoId);
          break;
        case window.YT.PlayerState.UNSTARTED:
        default:
          break;
      }

      this.lastPlayerTime = this.player.getCurrentTime();
      this.lastPlayerState = event.data;
    }
  };

  videoStarted(videoTitle, videoId) {
    let statement = new TinCan.Statement({
      actor: this.getActor(),
      verb: {
        id: "http://activitystrea.ms/schema/1.0/watch",
        display: { "en-US": "started watching" }
      },
      target: {
        id: "http://www.youtube.com/watch?v=" + videoId,
        definition: {
          name: {
            "en-US": localize("youtube_api_no_time_challenge", "en").format(
              videoTitle,
              this.youtubeUrlPrepended + videoId,
              this.props.contentTitle,
              this.props.contentId
            )
          }
        }
      }
    });

    this.lrs.saveStatement(statement, {
      callback: function(err, xhr) {
        // No error handling right now.
      }
    });
  }

  videoResumed(videoTitle, videoId) {
    let statement = new TinCan.Statement({
      actor: this.getActor(),
      verb: {
        id: "http://activitystrea.ms/schema/1.0/watch",
        display: { "en-US": "resumed watching" }
      },
      target: {
        id: "http://www.youtube.com/watch?v=" + videoId,
        definition: {
          name: {
            "en-US": localize("youtube_api_no_time_challenge", "en").format(
              videoTitle,
              this.youtubeUrlPrepended + videoId,
              this.props.contentTitle,
              this.props.contentId
            )
          }
        }
      }
    });

    this.lrs.saveStatement(statement, {
      callback: function(err, xhr) {
        // No error handling right now.
      }
    });
  }

  videoPausedAfterPlaying(videoTitle, videoId, timeStart, timeEnd) {
    let statement = new TinCan.Statement({
      actor: this.getActor(),
      verb: {
        id: "http://activitystrea.ms/schema/1.0/watch",
        display: { "en-US": "watched" }
      },
      target: {
        id: "http://www.youtube.com/watch?v=" + videoId,
        definition: {
          name: {
            "en-US": localize("youtube_api_time_diff_challenge", "en").format(
              videoTitle,
              this.youtubeUrlPrepended + videoId,
              this.props.contentTitle,
              this.props.contentId,
              timeStart,
              timeEnd
            )
          }
        }
      }
    });

    this.lrs.saveStatement(statement, {
      callback: function(err, xhr) {
        // No error handling right now.
      }
    });
  }

  videoSkipped(videoTitle, videoId, timeStart, timeEnd) {
    let statement = new TinCan.Statement({
      actor: this.getActor(),
      verb: {
        id: "http://activitystrea.ms/schema/1.0/watch",
        display: { "en-US": "skipped" }
      },
      target: {
        id: "http://www.youtube.com/watch?v=" + videoId,
        definition: {
          name: {
            "en-US": localize("youtube_api_time_diff_challenge", "en").format(
              videoTitle,
              this.youtubeUrlPrepended + videoId,
              this.props.contentTitle,
              this.props.contentId,
              timeStart,
              timeEnd
            )
          }
        }
      }
    });

    this.lrs.saveStatement(statement, {
      callback: function(err, xhr) {
        // No error handling right now.
      }
    });
  }

  videoTerminated(videoTitle, videoId) {
    let statement = new TinCan.Statement({
      actor: this.getActor(),
      verb: {
        id: "http://activitystrea.ms/schema/1.0/watch",
        display: { "en-US": "terminated" }
      },
      target: {
        id: "http://www.youtube.com/watch?v=" + videoId,
        definition: {
          name: {
            "en-US": localize("youtube_api_no_time_challenge", "en").format(
              videoTitle,
              this.youtubeUrlPrepended + videoId,
              this.props.contentTitle,
              this.props.contentId
            )
          }
        }
      }
    });

    this.lrs.saveStatement(statement, {
      callback: function(err, xhr) {
        // No error handling right now.
      }
    });
  }

  getActor() {
    return {
      name: this.props.user.name + " (Player ID: " + this.props.user.id + ")",
      account: {
        homePage: PLAYER_URL,
        name: this.props.user.name
      }
    };
  }

  render() {
    return (
      <div
        className={
          "iframe-container-challenge-profile" +
          (deviceServices.isIOS() ? " iframe-scroll-wrapper" : "")
        }
      >
        <div id={`youtube-player-${this.props.id}`}></div>
      </div>
    );
  }
}

YoutubePlayer.propTypes = propTypes;

export default YoutubePlayer;
